'use client';
import { useEffect, useMemo, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { usePathname } from 'next/navigation';
import { useQueryParam } from 'hooks/useQueryParam';
import { AdSlotName } from 'types/ads';
import styles from './AdSlot.module.scss';
import { keepName } from './AdSlotHelper';

const OUTBRAIN_TEMPLATE = 'DE_RTL.de';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    OBR?: any;
  }
}

export const DefaultAd = ({
  adName,
  className,
  minHeight,
  fullAdSlotName,
  type,
  hidePlaceholder = false,
}: {
  adName: string;
  className: string;
  minHeight: string;
  fullAdSlotName: string;
  type: string;
  hidePlaceholder?: boolean;
}) => {
  const [showPlaceholder, setShowPlaceholder] = useState(true);

  useEffect(() => {
    if (typeof window !== 'undefined' && window?.MoltenBundle !== undefined) {
      const names = adName === AdSlotName.HeaderKombi ? ['superbanner_1', 'wallpaper_1'] : [fullAdSlotName];

      window.MoltenBundle.cmd.push(() =>
        window.MoltenBundle?.registerAdPositionLoadedHandler(function (position) {
          if (names.indexOf(position.getName()) > -1 && position.containsAd()) {
            setShowPlaceholder(false);
          }
        })
      );
    }
  }, [adName, fullAdSlotName]);

  if (adName === AdSlotName.HeaderKombi) {
    return (
      <div
        className={clsx(className, 'ada-preservespace-superbanner_1-wallpaper_1')}
        style={minHeight !== 'auto' ? { minHeight: `${minHeight}` } : {}}
      >
        {showPlaceholder && !hidePlaceholder && <div className={styles.adPlaceholder}>- Anzeige -</div>}
        <div
          id="wallpaper_1"
          data-device={type}
          className={keepName(adName) ? '' : 'gujAd'}
          data-ada-container={`wallpaper_1_${type}`}
        />
        <div
          id="superbanner_1"
          data-device={type}
          className={keepName(adName) ? '' : 'gujAd'}
          data-ada-container={`superbanner_1_${type}`}
        />
      </div>
    );
  }

  return (
    <div className={className} style={minHeight !== 'auto' ? { minHeight: `${minHeight}` } : {}}>
      {showPlaceholder && !hidePlaceholder && <div className={styles.adPlaceholder}>- Anzeige -</div>}
      <div
        id={fullAdSlotName}
        data-device={type}
        className={keepName(adName) ? '' : 'gujAd'}
        data-ada-container={`${fullAdSlotName}_${type}`}
      />
    </div>
  );
};

export const OutbrainAd = ({
  widgetId = 'SF_5',
  className,
  fullAdSlotName,
}: {
  widgetId?: string;
  className?: string;
  fullAdSlotName?: string;
}) => {
  const pathName = usePathname();
  const device = useQueryParam('device')?.toLowerCase();
  const outbrainRef = useRef<HTMLDivElement>(null);

  const fullPath = `https://www.rtl.de${pathName}`;
  const isSmartfeed = widgetId === 'AR_10' || widgetId === 'AR_20';

  const userAdId = useMemo(() => {
    if (typeof document === 'undefined') return 'null';
    const adId = document.cookie
      .split('; ')
      .find((row) => row.startsWith('AdId='))
      ?.split('=')[1];
    return adId ?? 'null';
  }, []);

  useEffect(() => {
    const el = outbrainRef.current;
    if (el) {
      const target = el.querySelector('[data-ob-user-id]');
      if (target && userAdId) {
        target.setAttribute('data-ob-user-id', userAdId);
      }
    }

    if (window?.OBR) {
      window.OBR.extern.researchWidget();
    }
  }, [userAdId, pathName]);

  if (isSmartfeed && !!device && ['android', 'ios'].includes(device)) {
    return (
      <div
        ref={outbrainRef}
        className={className}
        data-ada-container={fullAdSlotName}
        data-ob-identifier={`ad-outbrain-${widgetId}`}
      >
        <div
          className="OUTBRAIN"
          data-src={fullPath}
          data-widget-id={device === 'ios' ? 'MB_1' : 'MB_11'}
          data-ob-user-id={userAdId}
          data-ob-installation-key={device === 'ios' ? 'DERTL2DL5GFLPIBD78HOPEBA6' : 'DERTL2I6IBI3O1OCGN7EMP9C3'}
        />
      </div>
    );
  }

  return (
    <div
      ref={outbrainRef}
      className={className}
      data-ada-container={fullAdSlotName}
      data-ob-identifier={`ad-outbrain-${widgetId}`}
    >
      <div className="OUTBRAIN" data-src={fullPath} data-widget-id={widgetId} data-ob-template={OUTBRAIN_TEMPLATE} />
    </div>
  );
};
